import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal, ImageReveal } from "../lib/motion";
import { CTASection } from "../components/site/CTASection";
import { ARTICLES, JOURNAL_CATEGORIES, formatDate } from "../data/journal";

export default function Journal() {
  const [cat, setCat] = useState("All");
  const list = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);
  const [lead, ...rest] = list;

  return (
    <>
      <Seo
        title="Journal | Arc & Hollow — Interior Design Writing, London"
        description="Perspectives on materials, craftsmanship, art in residential interiors, renovating period properties and the relationship between architecture and emotion."
        path="/journal"
        noindex
      />
      <PageHero
        overline="Journal"
        titleLines={["Writing on space,", "material and light."]}
        intro="A design publication in miniature — reflections on the thinking, materials and craft behind considered residential interiors."
      />

      <section className="container-x pb-24 md:pb-32">
        <div className="flex flex-wrap gap-x-2 gap-y-3 mb-14 border-b border-charcoal/15 pb-6">
          {JOURNAL_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`journal-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              aria-pressed={cat === c}
              className={`px-5 py-2.5 text-xs uppercase tracking-overline font-semibold transition-colors duration-400 ${
                cat === c ? "bg-charcoal text-chalk" : "text-olive hover:text-charcoal border border-charcoal/15"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {lead && (
          <Reveal>
            <Link to={`/journal/${lead.slug}`} className="group grid gap-8 lg:grid-cols-2 lg:gap-14 mb-20 items-center" data-testid={`journal-lead-${lead.slug}`}>
              <ImageReveal src={lead.hero} alt={lead.title} className="aspect-[4/3]" imgClassName="transition-transform duration-[1200ms] group-hover:scale-105" />
              <div>
                <p className="overline text-bronze">{lead.category} · {lead.readingTime} · {formatDate(lead.date)}</p>
                <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light leading-tight group-hover:text-bronze transition-colors duration-500">{lead.title}</h2>
                <p className="mt-6 text-lg font-light leading-relaxed text-olive">{lead.excerpt}</p>
                <span className="mt-8 inline-block overline link-underline">Read entry</span>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="grid gap-x-8 gap-y-14 md:grid-cols-3">
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.08}>
              <Link to={`/journal/${a.slug}`} className="group block" data-testid={`journal-item-${a.slug}`}>
                <ImageReveal src={a.hero} alt={a.title} className="aspect-[4/3]" imgClassName="transition-transform duration-[1200ms] group-hover:scale-105" />
                <p className="mt-5 overline text-bronze">{a.category} · {a.readingTime}</p>
                <h3 className="mt-3 font-serif text-2xl font-light group-hover:text-bronze transition-colors duration-500">{a.title}</h3>
                <p className="mt-2 text-sm text-olive font-light leading-relaxed">{a.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>

        {list.length === 0 && (
          <p className="text-center text-olive font-light py-20" data-testid="journal-empty">
            No entries in this category yet — new writing is added regularly.
          </p>
        )}
      </section>

      <CTASection />
    </>
  );
}
